import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Umbrella, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function MyLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'paid_leave',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [employeeId, setEmployeeId] = useState(null);

  useEffect(() => {
    fetchEmployeeAndLeaves();
  }, []);

  const fetchEmployeeAndLeaves = async () => {
    try {
      const statsResponse = await api.get('/employee/stats');
      setEmployeeId(statsResponse.data.employee_info.id);
      
      const leavesResponse = await api.get('/leaves', {
        params: { employee_id: statsResponse.data.employee_info.id }
      });
      setLeaves(leavesResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leaves', {
        employee_id: employeeId,
        ...formData
      });
      toast.success('Demande de congé envoyée');
      setOpen(false);
      resetForm();
      fetchEmployeeAndLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      leave_type: 'paid_leave',
      start_date: '',
      end_date: '',
      reason: ''
    });
  };

  const leaveTypes = {
    paid_leave: 'Congé payé',
    sick_leave: 'Congé maladie',
    unpaid_leave: 'Congé sans solde',
    maternity_leave: 'Congé maternité',
    paternity_leave: 'Congé paternité'
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { label: 'En attente', className: 'bg-warning text-white', icon: Clock },
      approved: { label: 'Approuvé', className: 'bg-success text-white', icon: CheckCircle },
      rejected: { label: 'Rejeté', className: 'bg-error text-white', icon: XCircle }
    };
    const variant = variants[status];
    const Icon = variant.icon;
    return (
      <Badge className={variant.className}>
        <Icon className="w-3 h-3 mr-1 inline" />
        {variant.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="my-leaves-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Mes Congés</h1>
          <p className="text-slate-600 mt-1">Demander et suivre mes congés</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-700" data-testid="request-leave-button">
              <Plus className="w-4 h-4 mr-2" />
              Demander un Congé
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nouvelle Demande de Congé</DialogTitle>
              <DialogDescription>
                Remplissez les informations
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="leave_type">Type de congé *</Label>
                <Select
                  value={formData.leave_type}
                  onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                >
                  <SelectTrigger data-testid="leave-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(leaveTypes).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Date début *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Date fin *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    data-testid="end-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Motif *</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                  rows={3}
                  data-testid="reason-input"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary" data-testid="submit-leave">
                  Soumettre
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Umbrella className="w-5 h-5 text-primary" />
            Mes Demandes de Congés
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Période</TableHead>
                  <TableHead className="font-semibold">Jours</TableHead>
                  <TableHead className="font-semibold">Motif</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                      Aucune demande de congé
                    </TableCell>
                  </TableRow>
                ) : (
                  leaves.map((leave) => (
                    <TableRow key={leave.id} className="hover:bg-slate-50">
                      <TableCell>{leaveTypes[leave.leave_type]}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {leave.start_date} → {leave.end_date}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {leave.days_count}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{leave.reason}</TableCell>
                      <TableCell>{getStatusBadge(leave.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}